<template>
  <div class="relative h-full w-full overflow-y-auto">
    <div class="flex flex-col">
      <div class="px-6 pb-3 flex items-center gap-4">
        <span class="text-slate-900 font-semibold text-[22px]">Searches</span>
        <span class="text-sm text-gray-500">
          {{ totalSearchesCount }} searches · {{ importSources?.length ?? 0 }} sources
        </span>
      </div>
    </div>

    <div class="px-6 pb-6 space-y-4">
      <div v-for="importSource in importSources" :key="importSource?.id" class="break-inside-avoid">
        <ImportSourceCard
          v-if="
            importSource?.id &&
            (isBuiltInImportSource(importSource.id) ||
              importSource.type === ImportSourceType.FIELD_SAFETY_NOTICES) &&
            ![
              BuiltInImportSourceId.HAND_SEARCH as string,
              BuiltInImportSourceId.CITATION_SEARCH as string,
            ].includes(importSource.id)
          "
          :import-source="importSource"
          :icon-url="importSource.iconUrl"
          :query="generateSearchQuery(importSource.id, review.entity.value)"
          :is-expanded="expandedSourceIds.has(importSource.id)"
          :source-type="getSourceGroupName(importSource.id)"
          :is-highlighted="highlightedSourceId === importSource.id"
          @toggle-expanded="handleToggleExpanded(importSource.id, $event)"
        />

        <ImportFromCustomSource
          v-else-if="
            ![BuiltInImportSourceId.HAND_SEARCH, BuiltInImportSourceId.CITATION_SEARCH].includes(
              importSource.id as BuiltInImportSourceId,
            )
          "
          :import-source="importSource"
          :is-expanded="expandedSourceIds.has(importSource.id)"
          :is-highlighted="highlightedSourceId === importSource.id"
          @toggle-expanded="handleToggleExpanded(importSource.id, $event)"
        />
      </div>
      <HandSearch
        v-if="
          review.entity.value.plan?.importPlan.importSources?.some(
            (i) => i.id === BuiltInImportSourceId.HAND_SEARCH,
          )
        "
        key="hand-search"
        :is-expanded="expandedSourceIds.has(BuiltInImportSourceId.HAND_SEARCH)"
        :is-highlighted="highlightedSourceId === BuiltInImportSourceId.HAND_SEARCH"
        @toggle-expanded="handleToggleExpanded(BuiltInImportSourceId.HAND_SEARCH, $event)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { injectStrict } from "@app/utils/injectStrict";
import { generateSearchQuery } from "@app/utils/generateSearchQuery/generateSearchQuery";
import { ReviewKey } from "../../../use-review";
import { computed, nextTick, ref, watch } from "vue";
import { BuiltInImportSourceId } from "@core/domain/types/builtInImportSourceId";
import ImportFromCustomSource from "./CustomImportSource/ImportFromCustomSource.vue";
import HandSearch from "./HandSearch/HandSearch.vue";
import useBuiltInImportSources from "@app/composables/use-built-in-import-sources";
import ImportSourceCard from "../Components/ImportSourceCard.vue";
import { GuidanceSourcesKey, SafetyInformationSourcesKey } from "@app/injectionKeys";
import { ImportSourceType } from "@core/domain/types/import-source-type.type";

const review = injectStrict(ReviewKey);
const builtInImportSources = useBuiltInImportSources();
const safetyInformationSources = injectStrict(SafetyInformationSourcesKey);
const guidanceSources = injectStrict(GuidanceSourcesKey);
const expandedSourceIds = ref<Set<string>>(new Set());
const highlightedSourceId = ref<string | null>(null);
const knownSourceIds = ref<Set<string>>(new Set());

function handleToggleExpanded(sourceId: string, event?: MouseEvent) {
  window.getSelection()?.removeAllRanges();
  const isShift = event?.shiftKey || false;
  if (isShift) {
    if (expandedSourceIds.value.has(sourceId)) {
      expandedSourceIds.value.delete(sourceId);
    } else {
      expandedSourceIds.value.add(sourceId);
    }
    expandedSourceIds.value = new Set(expandedSourceIds.value);
  } else {
    if (expandedSourceIds.value.has(sourceId)) {
      expandedSourceIds.value.delete(sourceId);
      expandedSourceIds.value = new Set(expandedSourceIds.value);
    } else {
      expandedSourceIds.value = new Set([sourceId]);
    }
  }
}

const importSources = computed(() => {
  return review.entity.value.plan?.importPlan.importSources
    ?.map((s) => {
      const source = { ...s };
      const builtInSource = builtInImportSources.findById(source.id);

      if (builtInSource) {
        source.iconUrl = builtInSource.iconUrl;
      } else {
        const safetyInformationSource = safetyInformationSources.find((x) => x.id === source.id);
        if (safetyInformationSource) {
          source.iconUrl = safetyInformationSource.country.flagUrl;
        }
      }

      return source;
    })
    .sort((a, b) => a.order - b.order)
    .sort((a, b) => {
      const aLength = review.searchesBySource.value?.[a.id]?.length || 0;
      const bLength = review.searchesBySource.value?.[b.id]?.length || 0;

      if (aLength > 0 && bLength === 0) return -1;
      if (aLength === 0 && bLength > 0) return 1;
      return 0;
    });
});

watch(
  importSources,
  (sources, prevSources) => {
    const ids = new Set((sources ?? []).map((s) => s.id));
    if (prevSources !== undefined) {
      const newSource = (sources ?? []).find((s) => !knownSourceIds.value.has(s.id));
      if (newSource) {
        highlightedSourceId.value = newSource.id;
        nextTick(() => {
          document
            .getElementById(newSource.id)
            ?.scrollIntoView({ behavior: "smooth", block: "nearest" });
        });
        setTimeout(() => {
          highlightedSourceId.value = null;
        }, 2500);
      }
    }
    knownSourceIds.value = ids;
  },
  { immediate: true },
);

const totalSearchesCount = computed(() => {
  return (
    importSources.value?.reduce((total, source) => {
      return total + (review.searchesBySource.value?.[source.id]?.length || 0);
    }, 0) || 0
  );
});

const isBuiltInImportSource = (importSourceId: string): importSourceId is BuiltInImportSourceId => {
  return Object.values(BuiltInImportSourceId).includes(importSourceId as BuiltInImportSourceId);
};

const SOURCE_GROUPS: Record<string, string> = {
  [BuiltInImportSourceId.PUBMED]: "Peer-reviewed literature",
  [BuiltInImportSourceId.OPEN_ALEX]: "Peer-reviewed literature",
  [BuiltInImportSourceId.DIMENSIONS_AI]: "Peer-reviewed literature",
  [BuiltInImportSourceId.PMC]: "Peer-reviewed literature",
  [BuiltInImportSourceId.EUROPE_PMC]: "Peer-reviewed literature",
  [BuiltInImportSourceId.EMBASE]: "Peer-reviewed literature",
  [BuiltInImportSourceId.LIVIVO]: "Peer-reviewed literature",
  [BuiltInImportSourceId.GOOGLE_SCHOLAR]: "Gray literature",
  [BuiltInImportSourceId.BIORXIV]: "Gray literature",
  [BuiltInImportSourceId.MEDRXIV]: "Gray literature",
  [BuiltInImportSourceId.COCHRANE]: "Systematic reviews",
  [BuiltInImportSourceId.PROSPERO]: "Systematic reviews",
  [BuiltInImportSourceId.ICTRP]: "Clinical trials",
  [BuiltInImportSourceId.OPEN_FDA]: "Safety: Incidents",
  [BuiltInImportSourceId.MAUDE]: "Safety: Incidents",
  [BuiltInImportSourceId.DAEN]: "Safety: Incidents",
  [BuiltInImportSourceId.MDID]: "Safety: Incidents",
  [BuiltInImportSourceId.HTA_DATABASE]: "Health technology assessments",
};

function getSourceGroupName(sourceId: string): string | undefined {
  if (SOURCE_GROUPS[sourceId]) return SOURCE_GROUPS[sourceId];
  if (guidanceSources.some((s) => s.id === sourceId)) return "Guidance";
  if (safetyInformationSources.some((s) => s.id === sourceId))
    return "Safety: FSNs, FSCAs, bans, recalls, etc...";
}
</script>
